import React from 'react';
import { useAccessibleClick } from '../../../hooks/use-accessible-click';
import { cn } from '../../../utils/cn';

interface SearchSuggestionItemProps {
    label: string;
    type?: string;
    query: string;
    onClick: () => void;
    variant?: 'default' | 'header';
    menuColor?: 'light' | 'dark';
    hasIntent?: boolean;
}

export const SearchSuggestionItem: React.FC<SearchSuggestionItemProps> = (
    props
) => {
    const {
        onClick,
        label,
        type,
        query,
        variant = 'default',
        menuColor = 'dark',
    } = props;
    const { onClick: handleClick, onKeyDown } = useAccessibleClick(onClick);

    return (
        <div
            onClick={handleClick}
            onKeyDown={onKeyDown}
            className={cn(
                'flex cursor-pointer items-center focus:outline-none md:p-2',
                variant === 'header' && menuColor === 'light'
                    ? 'md:p-0 md:focus:bg-white/10'
                    : variant === 'header' && menuColor === 'dark'
                      ? 'md:p-0 md:focus:bg-gray-200'
                      : 'md:hover:bg-gray-100 md:focus:bg-gray-100'
            )}
            role="option"
            tabIndex={0}
            aria-selected={false}
        >
            <div
                className={cn(
                    'flex min-h-4.5 max-w-full items-center gap-1 whitespace-nowrap leading-[1.1] md:min-h-0',
                    variant === 'header' && menuColor === 'light'
                        ? 'border-b border-transparent text-dropdown text-gray-100 hover:border-white'
                        : variant === 'header' && menuColor === 'dark'
                          ? 'border-b border-transparent text-dropdown text-gray-600 hover:border-gray-600'
                          : 'text-[#9b9b9b] md:text-gray-400'
                )}
            >
                <span className="flex-1 truncate">
                    {highlightMatch(label, query, variant, menuColor)}
                </span>
                <span
                    className={cn(
                        'truncate',
                        variant === 'header' && menuColor === 'light'
                            ? 'text-white md:text-dropdown'
                            : variant === 'header' && menuColor === 'dark'
                              ? 'text-gray-800 md:text-dropdown'
                              : 'text-gray-900'
                    )}
                >
                    {type ? ` in ${type}` : ''}
                </span>
            </div>
        </div>
    );
};

const highlightMatch = (
    text: string,
    query: string,
    variant: 'default' | 'header' = 'default',
    menuColor: 'light' | 'dark' = 'dark'
) => {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index !== -1) {
        const before = text.slice(0, index);
        const match = text.slice(index, index + query.length);
        const after = text.slice(index + query.length);
        return (
            <>
                <span
                    className={
                        variant === 'header' && menuColor === 'light'
                            ? 'py-0.5 text-dropdown text-gray-200'
                            : variant === 'header' && menuColor === 'dark'
                              ? 'py-0.5 text-dropdown text-gray-600'
                              : 'py-0.5 text-gray-400'
                    }
                >
                    {before}
                </span>
                <span
                    className={
                        variant === 'header' && menuColor === 'light'
                            ? 'py-0.5 text-dropdown text-white'
                            : variant === 'header' && menuColor === 'dark'
                              ? 'py-0.5 text-dropdown text-gray-800'
                              : 'py-0.5 text-gray-800'
                    }
                >
                    {match}
                </span>
                <span
                    className={
                        variant === 'header' && menuColor === 'light'
                            ? 'py-0.5 text-dropdown text-gray-200'
                            : variant === 'header' && menuColor === 'dark'
                              ? 'py-0.5 text-dropdown text-gray-600'
                              : 'py-0.5 text-gray-400'
                    }
                >
                    {after}
                </span>
            </>
        );
    }
    return (
        <span
            className={
                variant === 'header' && menuColor === 'light'
                    ? 'py-0.5 text-dropdown text-gray-200'
                    : variant === 'header' && menuColor === 'dark'
                      ? 'py-0.5 text-dropdown text-gray-600'
                      : 'py-0.5 text-gray-400'
            }
        >
            {text}
        </span>
    );
};
